import { RegistrationData } from "app/types/registrationTypes";
import { RegistrationFormScreens } from "app/utils/const";
import { useEffect, useState } from "react";
import cookie from "react-cookies";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import AccountDetails from "./AccountDetails/AccountDetails";
import AccountSettings from "./AccountSettings/AccountSettings";
import PersonalDetails from "./PersonalDetails/PersonalDetails";
import RegistrationProgressSteps from "./RegistrationProgressSteps/RegistrationProgressSteps";

type ModalContentsType = {
  isRegistrationModalOpen: boolean;
  getCountries: () => void;
  getCurrenciesList: () => void;
  getHeardAboutUs: () => void;
  submitRegistrationForm: (data: RegistrationData) => void;
  isCanadaSelected: boolean;
};

export const OtherOptions = {
  OPTION: "other",
  PREFIX: "Other - ",
};

const ModalContents = ({
  isRegistrationModalOpen,
  getCountries,
  getCurrenciesList,
  getHeardAboutUs,
  submitRegistrationForm,
  isCanadaSelected,
}: ModalContentsType): JSX.Element => {
  //
  const [formState, setFormState] = useState({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    country: "",
    state: "",
    city: "",
    address: "",
    building: "",
    postCode: "",
    phoneNumberPrefix: "",
    phoneNumberPrefixId: "0", // used to store default value for phone number prefix
    phoneNumberSuffix: "",
    phoneNumber: "",
    currency: "",
    heardAbout: "",
    promoCode: "",
    consentToMarketing: true,
    consentToTermsAndConditions: false,
    gRecaptchaResponse: "",
    expired: false,
    heardAboutOther: "",
  });

  const [selectedScreen, setSelectedScreen] = useState(
    RegistrationFormScreens.ACCOUNT_DETAILS
  );

  // set recaptcha verification response token for registration
  const handleVerify = (response: string) => {
    setFormState(prev => ({
      ...prev,
      gRecaptchaResponse: response,
      expired: false,
    }));
  };

  const handleExpire = () => {
    setFormState(prev => ({
      ...prev,
      expired: true,
    }));
  };

  useEffect(() => {
    getCountries();
    getCurrenciesList();
    getHeardAboutUs();
  }, [getCountries, getCurrenciesList, getHeardAboutUs]);

  const handleChange = (
    id: keyof typeof formState,
    value: string | boolean
  ) => {
    setFormState(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  useEffect(() => {
    setFormState(prev => ({
      ...prev,
      phoneNumber: prev.phoneNumberPrefix + prev.phoneNumberSuffix,
    }));
  }, [formState.phoneNumberPrefix, formState.phoneNumberSuffix]);

  const submitRegistration = () => {
    const data: RegistrationData = {
      username: formState.username,
      password: formState.password,
      email: formState.email,
      firstName: formState.firstName,
      lastName: formState.lastName,
      dateOfBirth: formState.dateOfBirth,
      contactNumber: formState.phoneNumber,
      addressLine1: isCanadaSelected ? formState.building : formState.address,
      addressLine2: isCanadaSelected ? formState.address : null,
      state: formState.state,
      city: formState.city,
      postCode: formState.postCode,
      country: formState.country,
      currency: formState.currency,
      gRecaptchaResponse: formState.gRecaptchaResponse,
      heardAboutUs:
        formState.heardAbout === OtherOptions.OPTION
          ? `${OtherOptions.PREFIX}${formState.heardAboutOther}`
          : formState.heardAbout,
      consentToMarketing: formState.consentToMarketing,
      promoCode: formState.promoCode,
      acceptedTCs: formState.consentToTermsAndConditions,
      gender: formState.gender,
      address: formState.address,
      affiliateType: cookie.load("affiliateType")
        ? cookie.load("affiliateType")
        : "",
    };

    submitRegistrationForm(data);
  };
  if (!isRegistrationModalOpen) return null;

  return (
    <div className="registration-form-modal__contents">
      <RegistrationProgressSteps selectedScreen={selectedScreen} />
      <AccountDetails
        username={formState.username}
        password={formState.password}
        email={formState.email}
        handleChange={handleChange}
        setSelectedScreen={setSelectedScreen}
        selectedScreen={selectedScreen}
      />
      <PersonalDetails
        firstName={formState.firstName}
        lastName={formState.lastName}
        handleChange={handleChange}
        dateOfBirth={formState.dateOfBirth}
        gender={formState.gender}
        country={formState.country}
        building={formState.building}
        state={formState.state}
        city={formState.city}
        address={formState.address}
        postCode={formState.postCode}
        phoneNumberPrefix={formState.phoneNumberPrefix}
        phoneNumberPrefixId={formState.phoneNumberPrefixId}
        phoneNumberSuffix={formState.phoneNumberSuffix}
        phoneNumber={formState.phoneNumber}
        setSelectedScreen={setSelectedScreen}
        selectedScreen={selectedScreen}
      />
      <AccountSettings
        handleVerify={handleVerify}
        handleExpire={handleExpire}
        handleChange={handleChange}
        setSelectedScreen={setSelectedScreen}
        currency={formState.currency}
        heardAbout={formState.heardAbout}
        promoCode={formState.promoCode}
        consentToMarketing={formState.consentToMarketing}
        consentToTermsAndConditions={formState.consentToTermsAndConditions}
        submitRegistration={submitRegistration}
        gRecaptchaResponse={formState.gRecaptchaResponse}
        heardAboutOther={formState.heardAboutOther}
        expired={formState.expired}
        selectedScreen={selectedScreen}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  isRegistrationModalOpen: state.registrationFormModel.isRegistrationModalOpen,
  isCanadaSelected: state.registrationFormModel.isCanadaSelected,
});

const mapDispatchToProps = dispatch => ({
  getCountries: () => dispatch.registrationFormModel.getCountries(),
  getCurrenciesList: () => dispatch.registrationFormModel.getCurrenciesList(),
  getHeardAboutUs: () => dispatch.registrationFormModel.getHeardAboutUs(),
  submitRegistrationForm: (data: RegistrationData) =>
    dispatch.registrationFormModel.submitRegistrationForm(data),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(ModalContents));
